import { useEffect, useState, useCallback } from "react";
import ReactFlow, {
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  Handle,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import { api } from "@/lib/api";
import { Plus, FloppyDisk, Trash, Lightning, ChatText, Tag, Path, Power } from "@phosphor-icons/react";

/* ============ Custom node renderers ============ */
const NODE_THEMES = {
  trigger: { bg: "var(--status-info)",     icon: Lightning, label: "Trigger" },
  reply:   { bg: "var(--status-success)",  icon: ChatText,  label: "Send Reply" },
  tag:     { bg: "var(--brand-accent)",    icon: Tag,       label: "Tag Lead" },
  branch:  { bg: "var(--brand-primary)",   icon: Path,      label: "Branch" },
};

function BaseNode({ data, type }) {
  const theme = NODE_THEMES[type] || NODE_THEMES.trigger;
  const Icon = theme.icon;
  return (
    <div className="bg-[var(--surface)] border-2 rounded-lg shadow-md min-w-[200px]" style={{ borderColor: theme.bg }}>
      {type !== "trigger" && <Handle type="target" position={Position.Left} style={{ background: theme.bg }} />}
      <div className="px-3 py-1.5 text-white text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ background: theme.bg }}>
        <Icon size={12} weight="fill" /> {theme.label}
      </div>
      <div className="p-2.5 text-[12px] text-[var(--text-primary)] space-y-1">
        <div className="font-semibold">{data.label || "(no label)"}</div>
        {data.body && <div className="text-[11px] text-[var(--text-secondary)] line-clamp-3">{data.body}</div>}
        {data.tag && <div className="text-[10px] font-mono bg-[var(--surface-2)] px-1.5 py-0.5 rounded inline-block">#{data.tag}</div>}
        {data.condition && <div className="text-[10px] font-mono text-[var(--text-muted)]">if: {data.condition}</div>}
      </div>
      <Handle type="source" position={Position.Right} style={{ background: theme.bg }} />
    </div>
  );
}
const nodeTypes = {
  trigger: (props) => <BaseNode {...props} type="trigger" />,
  reply:   (props) => <BaseNode {...props} type="reply" />,
  tag:     (props) => <BaseNode {...props} type="tag" />,
  branch:  (props) => <BaseNode {...props} type="branch" />,
};

/* ============ Helpers ============ */
const normalizeFlow = (flow) => {
  if (!flow) return flow;
  const nodes = (flow.nodes || []).map((n, i) => ({
    ...n,
    position: (n.position && typeof n.position.x === "number" && typeof n.position.y === "number")
      ? n.position
      : { x: 100 + i * 60, y: 100 + i * 40 },
    type: n.type || "trigger",
    data: n.data || { label: "(no label)" },
  }));
  return { ...flow, nodes, edges: flow.edges || [] };
};

/* ============ Main page ============ */
export default function FlowBuilder() {
  const [flows, setFlows] = useState([]);
  const [active, setActive] = useState(null);

  const loadFlows = async () => {
    const { data } = await api.get("/channel/flows");
    const normalized = data.map(normalizeFlow);
    setFlows(normalized);
    if (normalized.length && !active) setActive(normalized[0]);
  };
  useEffect(() => { loadFlows(); }, []);

  const createFlow = async () => {
    const name = window.prompt("Flow name?", "Untitled flow");
    if (!name) return;
    const { data } = await api.post("/channel/flows", {
      name,
      nodes: [{ id: "1", type: "trigger", position: { x: 100, y: 100 }, data: { label: "Inbound message" } }],
      edges: [],
    });
    await loadFlows();
    setActive(normalizeFlow(data));
  };

  const deleteFlow = async (id) => {
    if (!window.confirm("Delete this flow?")) return;
    await api.delete(`/channel/flows/${id}`);
    setActive(null);
    loadFlows();
  };

  return (
    <div className="flex h-[calc(100vh-64px)] bg-[var(--surface-2)]" data-testid="flow-builder-page">
      {/* LEFT — flow list */}
      <div className="w-64 bg-[var(--surface)] border-r border-[var(--border)] flex flex-col">
        <div className="p-3 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Channel Manager</div>
            <h2 className="font-display font-bold text-[15px]">Flow Builder</h2>
          </div>
          <button onClick={createFlow} data-testid="new-flow-btn" className="p-1.5 bg-[var(--brand-primary)] text-white rounded hover:opacity-90">
            <Plus size={14} weight="bold" />
          </button>
        </div>
        <div className="flex-1 overflow-auto">
          {flows.length === 0 && (
            <div className="p-4 text-[12px] text-[var(--text-muted)]" data-testid="flows-empty">
              No flows yet. Click + to create one.
            </div>
          )}
          {flows.map(f => (
            <button
              key={f.flow_id}
              data-testid={`flow-row-${f.flow_id}`}
              onClick={() => setActive(f)}
              className={`w-full text-left p-3 border-b border-[var(--border)] hover:bg-[var(--surface-2)] transition ${active?.flow_id === f.flow_id ? "bg-[var(--surface-3)]" : ""}`}>
              <div className="font-semibold text-[12px] truncate">{f.name}</div>
              <div className="flex items-center gap-1 mt-1">
                <Power size={10} weight={f.active ? "fill" : "regular"} className={f.active ? "text-[var(--status-success)]" : "text-[var(--text-muted)]"} />
                <span className="text-[10px] text-[var(--text-muted)]">{f.active ? "active" : "paused"} · {(f.nodes || []).length} nodes</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* RIGHT — canvas */}
      <div className="flex-1 flex flex-col">
        {!active && (
          <div className="flex-1 flex items-center justify-center text-[var(--text-muted)] text-[13px]">
            Select a flow on the left, or create a new one.
          </div>
        )}
        {active && (
          <ReactFlowProvider>
            <FlowCanvas key={active.flow_id} initial={active} onSaved={loadFlows} onDelete={deleteFlow} />
          </ReactFlowProvider>
        )}
      </div>
    </div>
  );
}

function FlowCanvas({ initial, onSaved, onDelete }) {
  const safe = normalizeFlow(initial);
  const [nodes, setNodes] = useState(safe.nodes || []);
  const [edges, setEdges] = useState(safe.edges || []);
  const [name, setName] = useState(safe.name);
  const [active, setActive] = useState(!!safe.active);
  const [triggers, setTriggers] = useState((safe.trigger_keywords || []).join(", "));
  const [selectedNode, setSelectedNode] = useState(null);
  const [saving, setSaving] = useState(false);

  const onNodesChange = useCallback((changes) => setNodes(n => applyNodeChanges(changes, n)), []);
  const onEdgesChange = useCallback((changes) => setEdges(e => applyEdgeChanges(changes, e)), []);
  const onConnect = useCallback((params) => setEdges(e => addEdge(params, e)), []);
  const onNodeClick = useCallback((_, node) => setSelectedNode(node), []);

  const addNode = (type) => {
    const id = String(Date.now());
    const defaults = {
      trigger: { label: "Inbound message", condition: "" },
      reply:   { label: "Send reply",      body: "Thanks for reaching out! Could you share your PIN code?" },
      tag:     { label: "Tag the lead",    tag: "qualified" },
      branch:  { label: "Branch",          condition: "contains 'price'" },
    }[type];
    setNodes(n => [...n, { id, type, position: { x: 200 + n.length * 60, y: 200 + n.length * 40 }, data: defaults }]);
  };

  const updateSelected = (patch) => {
    if (!selectedNode) return;
    setNodes(ns => ns.map(n => n.id === selectedNode.id ? { ...n, data: { ...n.data, ...patch } } : n));
    setSelectedNode(s => ({ ...s, data: { ...s.data, ...patch } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/channel/flows/${initial.flow_id}`, {
        name,
        active,
        trigger_keywords: triggers.split(",").map(s => s.trim()).filter(Boolean),
        nodes, edges,
      });
      onSaved();
    } catch (e) {
      alert("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  return (
    <>
      <div className="bg-[var(--surface)] border-b border-[var(--border)] px-4 py-2 flex items-center gap-3 flex-wrap">
        <input
          data-testid="flow-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="font-bold text-[15px] bg-transparent border-b border-transparent hover:border-[var(--border)] focus:border-[var(--brand-primary)] outline-none px-1 py-0.5 min-w-[200px]"
        />
        <label className="flex items-center gap-1.5 text-[11px]">
          <input data-testid="flow-active" type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} />
          Active
        </label>
        <div className="flex items-center gap-1.5 text-[11px]">
          <span className="text-[var(--text-muted)]">Trigger keywords:</span>
          <input
            data-testid="flow-triggers"
            value={triggers}
            onChange={(e) => setTriggers(e.target.value)}
            placeholder="spare parts, dealer, warranty"
            className="span-input text-[11px] h-7 min-w-[240px]"
          />
        </div>
        <div className="ml-auto flex gap-1.5">
          {["trigger", "reply", "tag", "branch"].map(t => (
            <button key={t} data-testid={`add-node-${t}`} onClick={() => addNode(t)} className="text-[11px] px-2 py-1 rounded font-semibold uppercase text-white" style={{ background: NODE_THEMES[t].bg }}>
              + {t}
            </button>
          ))}
          <button data-testid="save-flow-btn" disabled={saving} onClick={save} className="span-btn text-[11px] disabled:opacity-50">
            <FloppyDisk size={12} /> {saving ? "Saving…" : "Save"}
          </button>
          <button data-testid="delete-flow-btn" onClick={() => onDelete(initial.flow_id)} className="span-btn span-btn-ghost text-[11px] text-[var(--status-danger)]">
            <Trash size={12} />
          </button>
        </div>
      </div>

      <div className="flex-1 flex">
        <div className="flex-1 relative">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            fitView>
            <Background gap={16} />
            <Controls />
            <MiniMap zoomable pannable />
          </ReactFlow>
        </div>

        {selectedNode && (
          <div className="w-72 bg-[var(--surface)] border-l border-[var(--border)] p-4 space-y-3 overflow-auto" data-testid="node-editor">
            <div className="flex items-center justify-between">
              <div className="label-eyebrow">{selectedNode.type} node</div>
              <button onClick={() => setSelectedNode(null)} className="text-[var(--text-muted)] text-[14px]">×</button>
            </div>
            <div>
              <label className="block text-[10px] uppercase text-[var(--text-muted)] mb-1">Label</label>
              <input data-testid="node-label" value={selectedNode.data.label || ""} onChange={(e) => updateSelected({ label: e.target.value })} className="span-input w-full" />
            </div>
            {selectedNode.type === "reply" && (
              <div>
                <label className="block text-[10px] uppercase text-[var(--text-muted)] mb-1">Reply body</label>
                <textarea data-testid="node-body" rows={4} value={selectedNode.data.body || ""} onChange={(e) => updateSelected({ body: e.target.value })} className="span-input w-full text-[12px]" />
              </div>
            )}
            {selectedNode.type === "tag" && (
              <div>
                <label className="block text-[10px] uppercase text-[var(--text-muted)] mb-1">Tag name</label>
                <input data-testid="node-tag" value={selectedNode.data.tag || ""} onChange={(e) => updateSelected({ tag: e.target.value })} className="span-input w-full" />
              </div>
            )}
            {(selectedNode.type === "trigger" || selectedNode.type === "branch") && (
              <div>
                <label className="block text-[10px] uppercase text-[var(--text-muted)] mb-1">Condition</label>
                <input data-testid="node-condition" value={selectedNode.data.condition || ""} onChange={(e) => updateSelected({ condition: e.target.value })} placeholder="contains 'price'" className="span-input w-full" />
              </div>
            )}
            <button
              data-testid="delete-node-btn"
              onClick={() => {
                setNodes(ns => ns.filter(n => n.id !== selectedNode.id));
                setEdges(es => es.filter(e => e.source !== selectedNode.id && e.target !== selectedNode.id));
                setSelectedNode(null);
              }}
              className="w-full text-[11px] px-2 py-1.5 bg-[var(--status-danger)]/10 text-[var(--status-danger)] rounded hover:bg-[var(--status-danger)]/20">
              <Trash size={11} className="inline" /> Delete node
            </button>
          </div>
        )}
      </div>
    </>
  );
}
